import type { BeneficiaryAgencyScope } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryAgencyScope';
import resolveFacilityPlacement from '@UI/Pages/Beneficiaries/BeneficiaryList/Helpers/resolveFacilityPlacement';
import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';

export type AgencyScopeDescription = {
	/** Reads after "La recherche porte sur". */
	readonly label: string;
	readonly isSingleFacility: boolean;
};

const ALL_FACILITIES_DESCRIPTION: AgencyScopeDescription = {
	label: 'toutes vos agences',
	isSingleFacility: false,
};

/**
 * Describes the facilities a search covers. A caller assigned to one facility only reads that
 * facility's name, whatever the filter holds.
 */
const describeAgencyScope = (
	scope: BeneficiaryAgencyScope,
	organizations: readonly ComposedOrganization[],
): AgencyScopeDescription => {
	const facilities = organizations.flatMap((organization) => organization.facilities);
	const soleFacility = facilities.length === 1 ? facilities[0] : undefined;

	if (soleFacility !== undefined) {
		return { label: `l'agence ${soleFacility.name}`, isSingleFacility: true };
	}

	if (scope.type === 'facility') {
		const placement = resolveFacilityPlacement(scope.facilityId, organizations);

		return placement === null
			? ALL_FACILITIES_DESCRIPTION
			: { label: `l'agence ${placement.facilityName}`, isSingleFacility: true };
	}

	if (scope.type === 'organization') {
		const organization = organizations.find((candidate) => candidate.id === scope.organizationId);

		return organization === undefined
			? ALL_FACILITIES_DESCRIPTION
			: { label: `vos agences de la structure ${organization.name}`, isSingleFacility: false };
	}

	return ALL_FACILITIES_DESCRIPTION;
};

export default describeAgencyScope;
